import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { currencyCode, localeDate, tz } from '../../utils/region';
import StatutoryPackNote from './StatutoryPackNote';
import { CompanyDetail, BankAccount } from '../../types';
import WorkspacePortalAddress from './WorkspacePortalAddress';   // Workspace → Client portal address (ctrlplane)
import DocSeriesCard from './DocSeriesCard';
import { FirmMarketFields } from './FirmContactSettings';
import BizcalPolicyCard from './BizcalPolicyCard';   // firm policy — billing & reminders (multi-tenant package bizcal)

const input: React.CSSProperties = {
  width: '100%', padding: '9px 12px', fontSize: 13, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none',
};
const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 };
const card: React.CSSProperties = { background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 20, marginBottom: 16 };
const btn = (bg: string, color = '#fff'): React.CSSProperties => ({ padding: '8px 14px', borderRadius: 'var(--radius-md)', border: 'none', background: bg, color, fontSize: 13, fontWeight: 600, cursor: 'pointer' });

const readImage = (file: File, cb: (d: string) => void) => { const r = new FileReader(); r.onload = () => cb(String(r.result)); r.readAsDataURL(file); };

// Who uploaded / confirmed something, and when — the company's own zone, day-first, to the minute.
const stamp = (ts: number) => new Date((Number(ts) || 0) * 1000).toLocaleString(localeDate(), {
  timeZone: tz(), day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
});

// ── Bank account add/edit modal ─────────────────────────────────────────────
function BankForm({ companyId, account, onClose, onSaved }: { companyId: string; account: BankAccount | null; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const isNew = !account;
  const [accountType, setAccountType] = useState<'corporate' | 'personal' | 'client'>(account?.account_type || 'corporate');
  const [accountName, setAccountName] = useState(account?.account_name || '');
  const [accountNumber, setAccountNumber] = useState(account?.account_number || '');
  const [swiftCode, setSwiftCode] = useState(account?.swift_code || '');
  const [bankAddress, setBankAddress] = useState(account?.bank_address || '');
  // What an overseas transfer also needs (Bugify #e956363d). All optional: left empty, the line is
  // simply absent from the quotation / invoice payment card, exactly as before.
  const [bankName, setBankName] = useState(account?.bank_name || '');
  const [bankBranch, setBankBranch] = useState(account?.bank_branch || '');
  const [branchCode, setBranchCode] = useState(account?.branch_code || '');
  const [bankFullAddress, setBankFullAddress] = useState(account?.bank_full_address || '');
  const [qrCode, setQrCode] = useState(account?.qr_code || '');
  const [remark, setRemark] = useState(account?.remark || '');
  const [err, setErr] = useState(''); const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true); setErr('');
    const data = { accountType, accountName, accountNumber, swiftCode, bankAddress, bankName, bankBranch, branchCode, bankFullAddress, qrCode, remark };
    try {
      if (isNew) await api.adminAddBankAccount(companyId, data);
      else await api.adminUpdateBankAccount(companyId, account!.id, data);
      onSaved(); onClose();
    } catch (e: any) { setErr(e.message); setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: 520, maxHeight: '90vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', padding: 24, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16 }}>{isNew ? t('cs_add_bank') : t('edit')}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={lbl}>{t('cs_acct_type')}</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {(['corporate', 'personal'] as const).map(v => (
                <button key={v} onClick={() => setAccountType(v)} style={{ flex: 1, padding: '8px', borderRadius: 'var(--radius-md)', border: '1px solid', borderColor: accountType === v ? 'var(--accent)' : 'var(--border-strong)', background: accountType === v ? 'var(--accent)' : 'transparent', color: accountType === v ? '#fff' : 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  {t(v === 'corporate' ? 'cs_corporate' : 'cs_personal')}
                </button>
              ))}
            </div>
          </div>
          <div><label style={lbl}>{t('cs_account_name')}</label><input value={accountName} onChange={e => setAccountName(e.target.value)} style={input} /></div>
          <div><label style={lbl}>{t('cs_account_number')}</label><input value={accountNumber} onChange={e => setAccountNumber(e.target.value)} style={input} /></div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}><label style={lbl}>{t('cs_bank_name')}</label><input value={bankName} onChange={e => setBankName(e.target.value)} style={input} /></div>
            <div style={{ flex: 1 }}><label style={lbl}>{t('cs_bank_branch')}</label><input value={bankBranch} onChange={e => setBankBranch(e.target.value)} style={input} /></div>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}><label style={lbl}>{t('cs_branch_code')}</label><input value={branchCode} onChange={e => setBranchCode(e.target.value)} style={input} /></div>
            <div style={{ flex: 1 }}><label style={lbl}>{t('cs_swift')}</label><input value={swiftCode} onChange={e => setSwiftCode(e.target.value)} style={input} /></div>
          </div>
          <div><label style={lbl}>{t('cs_bank_full_address')}</label><input value={bankFullAddress} onChange={e => setBankFullAddress(e.target.value)} style={input} /></div>
          <div><label style={lbl}>{t('cs_bank_address')}</label><input value={bankAddress} onChange={e => setBankAddress(e.target.value)} style={input} /></div>
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.5, marginTop: -4 }}>{t('cs_bank_overseas_hint')}</div>
          <div>
            <label style={lbl}>{t('cs_qr')}</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {qrCode
                ? <img src={qrCode} alt="QR" style={{ width: 72, height: 72, objectFit: 'contain', borderRadius: 8, border: '1px solid var(--border)', background: '#fff' }} />
                : <div style={{ width: 72, height: 72, borderRadius: 8, border: '1px dashed var(--border-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-qrcode" style={{ fontSize: 22 }} /></div>}
              <label style={{ ...btn('var(--c-gray-100)', 'var(--text-secondary)'), display: 'inline-block' }}>
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) readImage(f, setQrCode); }} />
                {t('cs_upload')}
              </label>
              {qrCode && <button onClick={() => setQrCode('')} style={btn('transparent', '#E24B4A')}>{t('cs_remove')}</button>}
            </div>
          </div>
          <div><label style={lbl}>{t('cs_remark')}</label><textarea value={remark} onChange={e => setRemark(e.target.value)} rows={3} style={{ ...input, resize: 'vertical' }} /></div>
          {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)' }}>{err}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={onClose} style={btn('var(--c-gray-100)', 'var(--text-secondary)')}>{t('cancel')}</button>
            <button onClick={save} disabled={saving} style={btn('var(--accent)')}>{saving ? t('login_wait') : t('save')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Company settings (basic info + bank accounts) ───────────────────────────
export default function CompanySettings({ companyId, companyName, onBack, onChanged }: {
  companyId: string; companyName: string; onBack: () => void; onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [detail, setDetail] = useState<CompanyDetail | null>(null);
  const [err, setErr] = useState(''); const [msg, setMsg] = useState('');
  const [savingBasic, setSavingBasic] = useState(false);
  const [bankEditing, setBankEditing] = useState<BankAccount | null>(null);
  const [showBank, setShowBank] = useState(false);

  // basic-info fields
  const [f, setF] = useState({
    name: companyName, shortName: '', taxId: '', registerDate: '', vatRegistered: false,
    address: '', email: '', contactPerson: '', contactPhone: '', website: '', bookingUrl: '', logo: '', stamp: '',
    // per-firm settings (multi-tenant) — read by the server through services/firmSettings.ts
    officeEmail: '', ccEmails: '', ccIncludesOffice: true, receivingBankAccountId: '', firmKind: '', docSeriesLetter: '', timezone: '',
    nameTh: '', branchNo: '', paymentNotice: '',
  });
  const set = (k: string, v: any) => setF(prev => ({ ...prev, [k]: v }));
  const [regionOwn, setRegionOwn] = useState<RegionValues>({});   // this company's own region values ('' = inherit the workspace)
  // The VAT certificate (ภ.พ.20) beside the VAT toggle. `vatCert` is set only when the administrator
  // picks a new file or clears the saved one ({ file: '' } = remove); null leaves what is on file alone.
  const [vatCert, setVatCert] = useState<{ file: string; name: string } | null>(null);
  const [vatConfirmed, setVatConfirmed] = useState(false);
  const [vatWaived, setVatWaived] = useState(false);

  const load = async () => {
    try {
      const d: CompanyDetail = await api.adminGetCompany(companyId);
      setDetail(d);
      setF({
        name: d.name || '', shortName: d.short_name || '', taxId: d.tax_id || '', registerDate: d.register_date || '',
        vatRegistered: d.vat_registered === 1, address: d.address || '', email: d.email || '',
        contactPerson: d.contact_person || '', contactPhone: d.contact_phone || '', website: d.website || '',
        bookingUrl: d.booking_url || '',
        logo: d.logo || '', stamp: d.stamp || '',
        officeEmail: d.office_email || '', ccEmails: d.cc_emails || '', ccIncludesOffice: Number(d.cc_includes_office ?? 1) !== 0,
        receivingBankAccountId: d.receiving_bank_account_id || '',
        firmKind: d.firm_kind || '', docSeriesLetter: d.doc_series_letter || '', timezone: d.timezone || '',
        nameTh: d.name_th || '', branchNo: d.branch_no || '', paymentNotice: d.payment_notice || '',
      });
      setRegionOwn({ ...((d as any).regionOwn || {}) });
      setVatCert(null); setVatConfirmed(!!d.vat?.confirmed); setVatWaived(!!d.vat?.waived);
    } catch (e: any) { setErr(e.message); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const saveBasic = async () => {
    setSavingBasic(true); setErr(''); setMsg('');
    try {
      await api.adminUpdateCompany(companyId, {
        name: f.name, shortName: f.shortName, taxId: f.taxId, registerDate: f.registerDate,
        vatRegistered: f.vatRegistered, address: f.address, email: f.email,
        contactPerson: f.contactPerson, contactPhone: f.contactPhone, website: f.website,
        bookingUrl: f.bookingUrl,
        logo: f.logo, stamp: f.stamp,
        officeEmail: f.officeEmail, ccEmails: f.ccEmails, ccIncludesOffice: f.ccIncludesOffice, receivingBankAccountId: f.receivingBankAccountId,
        firmKind: f.firmKind, docSeriesLetter: f.docSeriesLetter,
        nameTh: f.nameTh, branchNo: f.branchNo, paymentNotice: f.paymentNotice,
        region: Object.fromEntries(REGION_KEYS.map(k => [k, regionOwn[k] || ''])),   // '' clears = inherit the workspace
        // VAT registration: the certificate goes with the tick that confirms it, and the server
        // refuses VAT = Y until both are there (server/src/services/firmVat.ts).
        vatConfirmed,
        vatWaived,
        ...(vatCert ? { vatCertificate: vatCert.file, vatCertificateName: vatCert.name } : {}),
      });
      setMsg(t('cs_saved')); onChanged();
      await load();   // the certificate panel shows who uploaded / confirmed it, and when
      setTimeout(() => setMsg(''), 2000);
    } catch (e: any) { setErr(e.message); } finally { setSavingBasic(false); }
  };

  const deleteBank = async (acc: BankAccount) => {
    if (!window.confirm(t('cs_confirm_delete_bank'))) return;
    await api.adminDeleteBankAccount(companyId, acc.id).catch((e: any) => setErr(e.message));
    load();
  };

  const imageField = (label: string, key: 'logo' | 'stamp') => (
    <div>
      <label style={lbl}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {(f as any)[key]
          ? <img src={(f as any)[key]} alt={label} style={{ width: 64, height: 64, objectFit: 'contain', borderRadius: 8, border: '1px solid var(--border)', background: '#fff' }} />
          : <div style={{ width: 64, height: 64, borderRadius: 8, border: '1px dashed var(--border-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-photo" style={{ fontSize: 20 }} /></div>}
        <label style={{ ...btn('var(--c-gray-100)', 'var(--text-secondary)'), display: 'inline-block' }}>
          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const file = e.target.files?.[0]; if (file) readImage(file, d => set(key, d)); }} />
          {t('cs_upload')}
        </label>
        {(f as any)[key] && <button onClick={() => set(key, '')} style={btn('transparent', '#E24B4A')}>{t('cs_remove')}</button>}
      </div>
    </div>
  );

  // VAT registration proof: what is on file, or the file the administrator has just picked.
  const vatInfo = detail?.vat || null;
  const certOnFile = vatInfo?.certificate || null;
  const certPresent = vatCert ? !!vatCert.file : !!certOnFile;
  const certLabel = (vatCert ? vatCert.name : certOnFile?.name) || '';
  const uploadedLine = certOnFile && certOnFile.at ? t('cs_vat_cert_uploaded', { at: stamp(certOnFile.at), name: certOnFile.byName || '—' }) : '';
  const confirmedLine = vatInfo && vatInfo.confirmed ? t('cs_vat_confirmed_by', { at: stamp(vatInfo.confirmedAt), name: vatInfo.confirmedByName || '—' }) : '';

  const field = (label: string, key: keyof typeof f, type = 'text') => (
    <div>
      <label style={lbl}>{label}</label>
      <input type={type} value={(f as any)[key]} onChange={e => set(key as string, e.target.value)} style={input} />
    </div>
  );

  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: 20 }}>
      <div style={{ maxWidth: 860, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <button onClick={onBack} style={{ ...btn('var(--c-gray-100)', 'var(--text-secondary)'), padding: '6px 12px' }}>
            <i className="ti ti-arrow-left" style={{ fontSize: 13, marginRight: 6 }} aria-hidden />{t('cs_companies_back')}
          </button>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{f.name || companyName}</div>
        </div>

        {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)', marginBottom: 12 }}>{err}</div>}

        {/* Basic info */}
        <div style={card}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 14 }}>{t('cs_basic_info')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {field(t('company_name'), 'name')}
            {field(t('cs_short_name'), 'shortName')}
            {field(t('cs_name_th'), 'nameTh')}
            {field(t('cs_branch_no'), 'branchNo')}
            {field(t('cs_tax_id'), 'taxId')}
            {field(t('cs_register_date'), 'registerDate', 'date')}
            {field(t('cs_email'), 'email')}
            {field(t('cs_website'), 'website')}
            {/* The firm's booking page: the address the AI email assistant offers instead of a placeholder. */}
            <div>
              <label style={lbl}>{t('cs_booking_url')}</label>
              <input value={f.bookingUrl} onChange={e => set('bookingUrl', e.target.value)} style={input} />
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{t('cs_booking_url_hint')}</div>
            </div>
            {field(t('cs_contact_person'), 'contactPerson')}
            {field(t('cs_contact_phone'), 'contactPhone')}
          </div>
          <div style={{ marginTop: 12 }}>{field(t('cs_address'), 'address')}</div>
          <div style={{ marginTop: 12 }}>
            <label style={lbl}>{t('cs_payment_notice')}</label>
            <textarea value={f.paymentNotice} onChange={e => set('paymentNotice', e.target.value)} rows={2} style={{ ...input, resize: 'vertical' }} />
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{t('cs_payment_notice_hint')}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 12, alignItems: 'end' }}>
            <div>
              <label style={lbl}>{t('cs_vat')}</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[false, true].map(v => (
                  <button key={String(v)} onClick={() => set('vatRegistered', v)} style={{ flex: 1, padding: '8px', borderRadius: 'var(--radius-md)', border: '1px solid', borderColor: f.vatRegistered === v ? 'var(--accent)' : 'var(--border-strong)', background: f.vatRegistered === v ? 'var(--accent)' : 'transparent', color: f.vatRegistered === v ? '#fff' : 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{v ? 'Y' : 'N'}</button>
                ))}
              </div>
            </div>
            {imageField(t('cs_logo'), 'logo')}
            {imageField(t('cs_stamp'), 'stamp')}
          </div>
          {/* The VAT certificate (ภ.พ.20) and the administrator's confirmation of it: VAT registered = Y
              needs both, and only then are this company's documents headed as tax invoices. */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12, alignItems: 'start' }}>
            <div>
              <label style={lbl}>{t('cs_vat_cert')}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: certPresent ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                  <i className={certPresent ? 'ti ti-file-check' : 'ti ti-file-off'} style={{ fontSize: 14, marginRight: 6 }} aria-hidden />
                  {certPresent ? (certLabel || t('cs_vat_cert_on_file')) : t('cs_vat_cert_none')}
                </span>
                <label style={{ ...btn('var(--c-gray-100)', 'var(--text-secondary)'), display: 'inline-block' }}>
                  <input type="file" accept="image/*,application/pdf" style={{ display: 'none' }}
                    onChange={e => { const file = e.target.files?.[0]; if (file) readImage(file, d => setVatCert({ file: d, name: file.name })); }} />
                  {certPresent ? t('on_replace') : t('cs_upload')}
                </label>
                {!vatCert && !!certOnFile && (
                  <button onClick={() => api.adminOpenVatCertificate(companyId).catch((e: any) => setErr(e.message))} style={btn('transparent', 'var(--accent)')}>{t('on_view')}</button>
                )}
                {certPresent && <button onClick={() => setVatCert({ file: '', name: '' })} style={btn('transparent', '#E24B4A')}>{t('cs_remove')}</button>}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{t('cs_vat_cert_hint')}</div>
              {/* Already marked Y from before this existed: ask for the certificate rather than block the form. */}
              {f.vatRegistered && !vatInfo?.ready && (
                <div style={{ fontSize: 11, color: '#BA7517', marginTop: 2 }}>
                  <i className="ti ti-alert-triangle" style={{ marginRight: 4 }} aria-hidden />{t('cs_vat_cert_missing')}
                </div>
              )}
              {vatCert?.file
                ? <div style={{ fontSize: 11, color: 'var(--accent)', marginTop: 2 }}>{t('cs_vat_cert_picked')}</div>
                : (!vatCert && uploadedLine
                  ? <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{uploadedLine}</div>
                  : null)}
            </div>
            <div>
              <label style={lbl}>{t('cs_vat_confirm')}</label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-primary)', cursor: 'pointer' }}>
                <input type="checkbox" checked={vatConfirmed} onChange={e => setVatConfirmed(e.target.checked)} />
                {t('cs_vat_confirm_tick')}
              </label>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{t('cs_vat_confirm_hint')}</div>
              {/* The owner's route for a company whose registration is real but whose ภ.พ.20 is not
                  going to be filed here. Sits under the confirmation because it REPLACES it, and
                  says who waived it for the same reason the confirmation names who confirmed. */}
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-primary)', cursor: 'pointer', marginTop: 10 }}>
                <input type="checkbox" checked={vatWaived} onChange={e => setVatWaived(e.target.checked)} />
                {t('cs_vat_waive_tick')}
              </label>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{t('cs_vat_waive_hint')}</div>
              {vatInfo?.waived && !!vatInfo.waivedAt && (
                <div style={{ fontSize: 11, color: '#BA7517', marginTop: 2 }}>
                  {t('cs_vat_waived_by', { at: stamp(vatInfo.waivedAt), name: vatInfo.waivedByName || '—' })}
                </div>
              )}
              {!vatCert && !!confirmedLine && (
                <div style={{ fontSize: 11, color: '#1D9E75', marginTop: 2 }}>
                  <i className="ti ti-check" style={{ marginRight: 4 }} aria-hidden />{confirmedLine}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
            {msg && <span style={{ fontSize: 12, color: '#1D9E75' }}><i className="ti ti-check" style={{ marginRight: 4 }} />{msg}</span>}
            <button onClick={saveBasic} disabled={savingBasic} style={btn('var(--accent)')}>{savingBasic ? t('login_wait') : t('save')}</button>
          </div>
        </div>

        {/* Firm settings — mailboxes, receiving account, kind (multi-tenant) */}
        <div style={card}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{t('cs_firm_settings')}</div>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 14 }}>{t('cs_firm_settings_hint')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>{t('cs_office_email')}</label>
              <input list="cs-mailboxes" value={f.officeEmail} onChange={e => set('officeEmail', e.target.value)} style={input} placeholder="office@…" />
              <datalist id="cs-mailboxes">{(detail?.mailboxes || []).map(m => <option key={m} value={m} />)}</datalist>
              <div style={{ fontSize: 11, color: f.officeEmail && !(detail?.mailboxes || []).includes(f.officeEmail.trim().toLowerCase()) ? '#E24B4A' : 'var(--text-tertiary)', marginTop: 4 }}>
                {f.officeEmail && !(detail?.mailboxes || []).includes(f.officeEmail.trim().toLowerCase()) ? t('cs_office_not_connected') : t('cs_office_email_hint')}
              </div>
            </div>
            <div>
              <label style={lbl}>{t('cs_cc_emails')}</label>
              <input value={f.ccEmails} onChange={e => set('ccEmails', e.target.value)} style={input} placeholder="a@…, b@…" />
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{t('cs_cc_emails_hint')}</div>
              {/* Sits under the Cc field because it qualifies it: the office mailbox is copied on top
                  of whatever is typed above, which is invisible until you see a sent message. */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 8, fontSize: 12, cursor: 'pointer' }}>
                <input type="checkbox" checked={f.ccIncludesOffice} onChange={e => set('ccIncludesOffice', e.target.checked)} />
                <span>{t('cs_cc_includes_office')}</span>
              </label>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{t('cs_cc_includes_office_hint')}</div>
            </div>
            <div>
              <label style={lbl}>{t('cs_receiving_account')}</label>
              <select value={f.receivingBankAccountId} onChange={e => set('receivingBankAccountId', e.target.value)} style={input}>
                <option value="">{t('cs_receiving_none')}</option>
                {(detail?.bankAccounts || []).map(a => (
                  <option key={a.id} value={a.id}>{t(a.account_type === 'corporate' ? 'cs_corporate' : a.account_type === 'client' ? 'cs_client' : 'cs_personal')} · {a.account_name || '—'} · {a.account_number}{a.currency && a.currency !== currencyCode() ? ` (${a.currency})` : ''}</option>
                ))}
              </select>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{t('cs_receiving_account_hint')}</div>
            </div>
            <div>
              <label style={lbl}>{t('cs_firm_kind')}</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {(['legal', 'accounting'] as const).map(k => {
                  const on = f.firmKind.split(',').map(x => x.trim()).includes(k);
                  return (
                    <button key={k} type="button" onClick={() => { const cur = f.firmKind.split(',').map(x => x.trim()).filter(Boolean); set('firmKind', (on ? cur.filter(x => x !== k) : [...cur, k]).join(',')); }}
                      style={{ flex: 1, padding: '8px', borderRadius: 'var(--radius-md)', border: '1px solid', borderColor: on ? 'var(--accent)' : 'var(--border-strong)', background: on ? 'var(--accent-soft, var(--bg-active))' : 'var(--c-gray-50)', color: on ? 'var(--accent)' : 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                      {t(k === 'legal' ? 'cs_kind_legal' : 'cs_kind_accounting')}
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{t('cs_firm_kind_hint')}</div>
            </div>
            <div>
              <label style={lbl}>{t('cs_series_letter')}</label>
              <input value={f.docSeriesLetter} onChange={e => set('docSeriesLetter', e.target.value.toUpperCase())} style={{ ...input, width: 120 }} maxLength={3} placeholder="L" />
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{t('cs_series_letter_hint')}</div>
            </div>
          </div>
          {/* This company's own region (owner 2026-10-03): dropdowns from the country packs; empty = the workspace's value */}
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', margin: '18px 0 2px' }}>{t('cs_co_region')}</div>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 10 }}>{t('cs_co_region_hint')}</div>
          <RegionFields value={regionOwn} onChange={setRegionOwn} inherit={(((detail as any)?.regionEffective) as RegionValues) || {}} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
            {msg && <span style={{ fontSize: 12, color: '#1D9E75' }}><i className="ti ti-check" style={{ marginRight: 4 }} />{msg}</span>}
            <button onClick={saveBasic} disabled={savingBasic} style={btn('var(--accent)')}>{savingBasic ? t('login_wait') : t('save')}</button>
          </div>
        </div>

        <DocSeriesCard companyId={companyId} letter={f.docSeriesLetter} />
        <FirmMarketFields companyId={companyId} />
        {/* Bank accounts */}
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{t('cs_bank_accounts')}</div>
            <div style={{ flex: 1 }} />
            <button onClick={() => { setBankEditing(null); setShowBank(true); }} style={btn('var(--accent)')}>
              <i className="ti ti-plus" style={{ fontSize: 13, marginRight: 6 }} aria-hidden />{t('cs_add_bank')}
            </button>
          </div>
          {(detail?.bankAccounts || []).length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-tertiary)', padding: '12px 0' }}>{t('cs_no_bank')}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {detail!.bankAccounts.map(a => (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 14, border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 12 }}>
                  {a.qr_code
                    ? <img src={a.qr_code} alt="QR" style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 6, border: '1px solid var(--border)', background: '#fff' }} />
                    : <div style={{ width: 48, height: 48, borderRadius: 6, background: 'var(--bg-active)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-building-bank" /></div>}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                      {a.account_name || '—'}
                      <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 600, padding: '1px 8px', borderRadius: 10, background: 'var(--bg-active)', color: 'var(--text-secondary)' }}>
                        {t(a.account_type === 'corporate' ? 'cs_corporate' : 'cs_personal')}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{a.account_number}{a.swift_code ? ' · ' + a.swift_code : ''}</div>
                  </div>
                  <button onClick={() => { setBankEditing(a); setShowBank(true); }} title={t('edit')} style={{ border: 'none', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}><i className="ti ti-pencil" /></button>
                  <button onClick={() => deleteBank(a)} title={t('delete')} style={{ border: 'none', background: 'none', color: '#E24B4A', cursor: 'pointer' }}><i className="ti ti-trash" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
        <BizcalPolicyCard companyId={companyId} />
      </div>

      {showBank && <BankForm companyId={companyId} account={bankEditing} onClose={() => setShowBank(false)} onSaved={load} />}
    </div>
  );
}

// ── Region fields (owner 2026-10-03): country / time zone / currency … are DROPDOWNS fed by the server's
// country packs (ten countries by default). Picking a country pre-fills the rest; each field stays editable.
// `inherit` = the company card: an empty value means "the workspace's", shown as the effective value.
type RegionValues = Record<string, string>;
type Packs = { packs: any[]; timezones: string[]; currencies: { code: string; symbol: string }[]; dateFormats: string[]; calendars: string[] };
const REGION_KEYS = ['country', 'timezone', 'currency', 'currencySymbol', 'calendar', 'dateFormat', 'dialCode', 'localeNumber', 'localeDate'];
// Exported for Admin → Portal accounts (a merchant's own region, package portalintl); `loadPacks` lets a
// page outside the admin-only API fetch the same pack list from its own route.
export function RegionFields({ value, onChange, inherit, loadPacks }: { value: RegionValues; onChange: (next: RegionValues) => void; inherit?: RegionValues; loadPacks?: () => Promise<any> }) {
  const { t } = useTranslation();
  const [packs, setPacks] = useState<Packs | null>(null);
  useEffect(() => { (loadPacks || api.adminCountryPacks)().then((d: any) => setPacks(d)).catch(() => setPacks({ packs: [], timezones: [], currencies: [], dateFormats: ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'], calendars: ['gregorian', 'buddhist-dual'] })); }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k: string, v: string) => onChange({ ...value, [k]: v });
  const pickCountry = (code: string) => {
    const pk = (packs?.packs || []).find((x: any) => x.country === code);
    if (!pk) { onChange({ ...value, country: code }); return; }
    onChange({ ...value, country: code, timezone: pk.timezones[0] || '', currency: pk.currency, currencySymbol: pk.currencySymbol, calendar: pk.calendar, dateFormat: pk.dateFormat, dialCode: pk.dialCode, localeNumber: pk.localeNumber, localeDate: pk.localeDate });
  };
  const inh = (k: string) => (inherit ? `${t('cs_inherit')}${inherit[k] ? ` (${inherit[k]})` : ''}` : '—');
  const withCurrent = (list: string[], cur: string) => (cur && !list.includes(cur) ? [cur, ...list] : list);
  const sel = (k: string, options: { v: string; label: string }[]) => (
    <select value={value[k] || ''} onChange={e => set(k, e.target.value)} style={input}>
      <option value="">{inh(k)}</option>
      {options.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
    </select>
  );
  const dial = Array.from(new Set((packs?.packs || []).map((x: any) => String(x.dialCode))));
  const curSym = (c: string) => (packs?.currencies || []).find(x => x.code === c)?.symbol || '';
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
      <div><label style={lbl}>{t('cs_country')}</label>
        <select value={value.country || ''} onChange={e => pickCountry(e.target.value)} style={input}>
          <option value="">{inh('country')}</option>
          {(packs?.packs || []).map((x: any) => <option key={x.country} value={x.country}>{x.name} ({x.country})</option>)}
          {value.country && !(packs?.packs || []).some((x: any) => x.country === value.country) && <option value={value.country}>{value.country}</option>}
        </select>
        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{t('cs_country_hint')}</div>
        <StatutoryPackNote pack={(packs?.packs || []).find((x: any) => x.country === (value.country || inherit?.country))} /></div>
      <div><label style={lbl}>{t('cs_timezone')}</label>{sel('timezone', withCurrent(packs?.timezones || [], value.timezone || '').map(z => ({ v: z, label: z })))}</div>
      <div><label style={lbl}>{t('cs_currency')}</label>{sel('currency', withCurrent((packs?.currencies || []).map(c => c.code), value.currency || '').map(c => ({ v: c, label: curSym(c) ? `${c} · ${curSym(c)}` : c })))}</div>
      <div><label style={lbl}>{t('cs_currency_symbol')}</label><input value={value.currencySymbol || ''} onChange={e => set('currencySymbol', e.target.value)} style={input} placeholder={inherit ? inh('currencySymbol') : '฿'} /></div>
      <div><label style={lbl}>{t('cs_calendar')}</label>{sel('calendar', (packs?.calendars || ['gregorian', 'buddhist-dual']).map(c => ({ v: c, label: c === 'gregorian' ? t('cs_calendar_gregorian') : t('cs_calendar_buddhist') })))}</div>
      <div><label style={lbl}>{t('cs_date_format')}</label>{sel('dateFormat', (packs?.dateFormats || []).map(f => ({ v: f, label: f })))}</div>
      <div><label style={lbl}>{t('cs_dial_code')}</label>{sel('dialCode', withCurrent(dial, value.dialCode || '').map(d => ({ v: d, label: d })))}</div>
      <div><label style={lbl}>{t('cs_locale_number')}</label>{sel('localeNumber', withCurrent(Array.from(new Set((packs?.packs || []).map((x: any) => String(x.localeNumber)))), value.localeNumber || '').map(l => ({ v: l, label: l })))}</div>
      <div><label style={lbl}>{t('cs_locale_date')}</label>{sel('localeDate', withCurrent(Array.from(new Set((packs?.packs || []).map((x: any) => String(x.localeDate)))), value.localeDate || '').map(l => ({ v: l, label: l })))}</div>
    </div>
  );
}

// ── Workspace settings (deployment-wide: one deployment = one tenant) ────────────────────────
// Shared CC list copied on every firm email, the public base URL for no-login links, the portal's
// no-reply sender, the brand word emails sign with. Read by the server through services/firmSettings.ts.
export function WorkspaceSettings() {
  const { t } = useTranslation();
  const [w, setW] = useState({ ccEmails: '', publicBaseUrl: '', publicBaseFromEnv: false, noreplyEmail: '', brandName: '', brandNameEffective: '', acctWorkspace: false });
  const [r, setR] = useState<Record<string, string>>({ timezone: '', currency: '', currencySymbol: '', localeNumber: '', localeDate: '', calendar: '', dateFormat: '', country: '', dialCode: '' });
  const setRegion = (k: string, v: string) => setR(prev => ({ ...prev, [k]: v }));
  const [world, setWorld] = useState<{ ids: string[]; saved: boolean; options: { id: string; name: string }[] }>({ ids: [], saved: false, options: [] });
  const takeWorld = (d: any) => setWorld({ ids: Array.isArray(d?.sharedWorld) ? d.sharedWorld : [], saved: !!d?.sharedWorldSaved, options: Array.isArray(d?.sharedWorldOptions) ? d.sharedWorldOptions : [] });
  const [err, setErr] = useState(''); const [msg, setMsg] = useState(''); const [saving, setSaving] = useState(false);
  // ⛔ (Owner 2026-11-03, the one-line merge — review round 1) Save sends every field of this card, so it waits for the GET:
  // before it answers (or when it failed) the form holds placeholders, and a Save would overwrite the stored CC list, brand,
  // region, shared world — and the workspace switch, which now also carries three finance rules. `loadedWs` is the switch as
  // the server last said it; Save sends acctWorkspace only when the box was actually changed from it.
  const [loaded, setLoaded] = useState(false);
  const [loadedWs, setLoadedWs] = useState(false);
  useEffect(() => { api.adminWorkspaceSettings().then((d: any) => { takeWorld(d); setW({ ccEmails: d.ccEmails || '', publicBaseUrl: d.publicBaseUrl || '', publicBaseFromEnv: !!d.publicBaseFromEnv, noreplyEmail: d.noreplyEmail || '', brandName: d.brandName || '', brandNameEffective: d.brandNameEffective || '', acctWorkspace: !!d.acctWorkspace }); setLoadedWs(!!d.acctWorkspace); if (d.region) setR(d.region); setLoaded(true); }).catch((e: any) => setErr(e.message)); }, []);
  const save = async () => {
    if (!loaded) return;
    setSaving(true); setErr(''); setMsg('');
    try {
      const d: any = await api.adminSaveWorkspaceSettings({ ccEmails: w.ccEmails, noreplyEmail: w.noreplyEmail, brandName: w.brandName, region: r, sharedWorld: world.ids, ...(w.acctWorkspace !== loadedWs ? { acctWorkspace: w.acctWorkspace } : {}), ...(w.publicBaseFromEnv ? {} : { publicBaseUrl: w.publicBaseUrl }) });
      takeWorld(d);
      setW({ ccEmails: d.ccEmails || '', publicBaseUrl: d.publicBaseUrl || '', publicBaseFromEnv: !!d.publicBaseFromEnv, noreplyEmail: d.noreplyEmail || '', brandName: d.brandName || '', brandNameEffective: d.brandNameEffective || '', acctWorkspace: !!d.acctWorkspace }); setLoadedWs(!!d.acctWorkspace); if (d.region) setR(d.region);
      setMsg(t('cs_saved')); setTimeout(() => setMsg(''), 2000);
    } catch (e: any) { setErr(e.message); } finally { setSaving(false); }
  };
  return (
    <div style={{ ...card, marginBottom: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{t('cs_workspace')}</div>
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 14 }}>{t('cs_workspace_hint')}</div>
      {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)', marginBottom: 12 }}>{err}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={lbl}>{t('cs_workspace_cc')}</label>
          <input value={w.ccEmails} onChange={e => setW({ ...w, ccEmails: e.target.value })} style={input} placeholder="office@…" />
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{t('cs_workspace_cc_hint')}</div>
        </div>
        <div>
          <label style={lbl}>{t('cs_public_base')}</label>
          <input value={w.publicBaseUrl} disabled={w.publicBaseFromEnv} onChange={e => setW({ ...w, publicBaseUrl: e.target.value })} style={{ ...input, opacity: w.publicBaseFromEnv ? 0.7 : 1 }} placeholder="https://…" />
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{w.publicBaseFromEnv ? t('cs_public_base_env') : t('cs_public_base_hint')}</div>
        </div>
        <div>
          <label style={lbl}>{t('cs_noreply')}</label>
          <input value={w.noreplyEmail} onChange={e => setW({ ...w, noreplyEmail: e.target.value })} style={input} placeholder="noreply@…" />
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{t('cs_noreply_hint')}</div>
        </div>
        <div>
          <label style={lbl}>{t('cs_brand')}</label>
          <input value={w.brandName} onChange={e => setW({ ...w, brandName: e.target.value })} style={input} maxLength={60} placeholder={w.brandNameEffective || '…'} />
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{t('cs_brand_hint')}</div>
        </div>
      </div>
      <WorkspacePortalAddress />
      {/* Region — the jurisdiction this tenant operates in (services/region.ts) */}
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', margin: '18px 0 2px' }}>{t('cs_region')}</div>
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 10 }}>{t('cs_region_hint')}</div>
      <RegionFields value={r} onChange={next => setR(next)} />
      {/* Shared world — which companies work as one office (server services/companyScope.ts) */}
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', margin: '18px 0 2px' }}>{t('cs_shared_world')}</div>
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 8 }}>{t('cs_shared_world_hint')}</div>
      {!world.saved && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 6 }}>{t('cs_shared_world_default')}</div>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px' }}>
        {world.options.map(o => (
          <label key={o.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-primary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={world.ids.includes(o.id)}
              onChange={e => setWorld(prev => ({ ...prev, ids: e.target.checked ? [...prev.ids, o.id] : prev.ids.filter(x => x !== o.id) }))} />
            {o.name}
          </label>
        ))}
      </div>
      {/* Company accounting workspace (owner 2026-10-13) — the app_settings key acct_workspace. An
          installation-wide switch, not a company column: one installation = one tenant, and the
          workspace is a different way of presenting the same accounting_books module, so it is
          decided once per deployment. Off = the product default (the single statutory-books page). */}
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', margin: '18px 0 2px' }}>{t('cs_acct_workspace')}</div>
      <label style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: 'var(--text-primary)', cursor: 'pointer' }}>
        <input type="checkbox" checked={w.acctWorkspace} onChange={e => setW({ ...w, acctWorkspace: e.target.checked })} style={{ marginTop: 3 }} />
        <span>
          {t('cs_acct_workspace_on')}
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{t('cs_acct_workspace_hint')}</div>
        </span>
      </label>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
        {msg && <span style={{ fontSize: 12, color: '#1D9E75' }}><i className="ti ti-check" style={{ marginRight: 4 }} />{msg}</span>}
        <button onClick={save} disabled={saving || !loaded} style={btn('var(--accent)')}>{saving ? t('login_wait') : t('save')}</button>
      </div>
    </div>
  );
}
